import { useRef, useEffect, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty, isString, isUndefined } from 'lodash';
import classNames from 'classnames';
// eslint-disable-next-line import/no-named-as-default
import List from 'react-virtualized/dist/commonjs/List';
// Import UI Components
import { Option, Empty, ExtendedInput, BusyLoader, Tooltip } from '@geneui/components';
// Import Constants
import { l } from 'constants/common';

export interface OptionListContentProps {
    list: AnyObject[];
    keys: string[];
    titles?: string[];
    rowCount?: number;
    optionValue?: Nilable<string | number>;
    optionSelectHandler: (item: AnyObject) => void;
    contentTop?: number;
    listMinWidth?: number;
    listWidth?: number;
    listHeight?: number;
    listRowHeight?: number;
    selectedPropertyKey?: string;
    emptySize?: 'big' | 'small';
    hideTitleWrapper?: boolean;
    isLoading?: boolean;
    searchValue?: string;
    searchHandler?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    renderCustomSearch?: () => React.ReactNode;
}

const OptionListContent = (props: OptionListContentProps) => {
    const {
        list,
        keys,
        titles,
        rowCount = 0,
        searchHandler,
        optionValue,
        contentTop = 50,
        optionSelectHandler,
        listMinWidth,
        listWidth = 258,
        listHeight = 240,
        listRowHeight = 40,
        selectedPropertyKey = 'value',
        searchValue = '',
        hideTitleWrapper = false,
        emptySize = 'big',
        isLoading = false,
        renderCustomSearch,
    } = props;
    const { t } = useTranslation();
    const searchRef = useRef<HTMLInputElement>(null);

    const getRow = (item: AnyObject) => {
        if (!isUndefined(item)) {
            return keys.map((element, index) => {
                return (
                    <Tooltip
                        key={`${item[selectedPropertyKey]}_${index}`}
                        title={isString(item[element]) ? item[element] : null}
                    >
                        <span className={`crm-option-column-${index} ellipsis-text`}>{item[element]}</span>
                    </Tooltip>
                );
            });
        }
    };

    const getTitle = () => {
        return titles?.map((element, index) => {
            return (
                <span key={element} className={`crm-option-header-column-${index}`}>
                    {element}
                </span>
            );
        });
    };

    const init = () => {
        searchRef.current?.focus();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, []);

    return (
        <div className="crm-option-list-content" style={{ top: `${contentTop}px`, minWidth: listMinWidth ?? 'auto' }}>
            {renderCustomSearch ? (
                renderCustomSearch()
            ) : (
                <ExtendedInput
                    ref={searchRef}
                    className="crm-option-list-content-search"
                    value={searchValue}
                    onChange={searchHandler}
                />
            )}

            {!hideTitleWrapper && (
                <Option title={getTitle()} border="top" className="crm-option-list-content-column-names" />
            )}

            <div
                className={classNames('crm-option-list-content__option-list', {
                    'small-empty-option-list': isEmpty(list) && emptySize === 'small',
                })}
            >
                <BusyLoader isBusy={isLoading}>
                    {!isLoading && (
                        <>
                            {!isEmpty(list) ? (
                                <List
                                    width={listWidth}
                                    height={listHeight}
                                    rowCount={rowCount}
                                    rowHeight={listRowHeight}
                                    rowRenderer={({ index, style }) => {
                                        return (
                                            <Option
                                                style={style}
                                                key={list[index]?.[selectedPropertyKey]}
                                                leftCustomElement={getRow(list[index])}
                                                onClick={() => optionSelectHandler(list[index])}
                                                active={list[index]?.[selectedPropertyKey] === optionValue}
                                            />
                                        );
                                    }}
                                />
                            ) : (
                                <Empty title={t(l.NoDataToDisplay)} size={emptySize} />
                            )}
                        </>
                    )}
                </BusyLoader>
            </div>
        </div>
    );
};

export default memo(OptionListContent);
